// SubIdentityGate — the clinic-mailbox sign-in screen.
//
// A shared clinic mailbox is one Entra login used by several people. Before any
// page renders, this gate makes the person say who they are and prove it with a
// personal PIN. It uses the same frosted overlay + numeric keypad as the
// workstation PIN lock (which clinic mailboxes are EXEMPT from — this PIN is
// their lock, see SubSessionGuard below); a dropdown at the top selects the user. On (re)lock it defaults to no
// selection. Renders IN PLACE OF `children` until signed in (fail-closed).
//
// ASSUMED IDENTITY: a Supervisor viewing the app as a clinic mailbox still needs
// to say WHICH person's view (the mailbox owns no cases), but must never be
// asked for that person's PIN — they cannot know it, and could not set or verify
// one anyway (every non-GET is rejected while assuming). So the gate becomes a
// plain picker: choosing a name sets the impersonation sub-identity, and the
// server re-checks it against the clinic's active roster. Because this overlay
// replaces the whole app — banner included — it carries its own always-available
// Exit, so an assumed mailbox can never become a dead end.
//
// LAPSED SESSION: the server ends a PIN session after 60 idle minutes, but the
// page and its token stay put (a form half-filled, a tab left in the
// background). SubSessionGuard checks the session on every navigation, when
// the tab comes back into view and once a minute — with a PEEK that does not
// count as activity — and a refused write (coded 403) triggers it too. Real
// interaction (keys, clicks, typing) DOES count: at most every few minutes it
// slides the server expiry, so the timeout means 60 minutes of actual
// inactivity, not 60 minutes without an API call mid-form. A lapsed
// session gets the PIN pad OVER the page: nothing unmounts, so typed input
// survives, and a refused submit retries once the PIN is in.

import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import { getClinicContext, getClinicSessionStatus, requestClinicRoster, setSubPin, verifySubSession, requestSubPinReset, dismissRosterDeclined } from '../api/client.js';
import {
  setSubSession, getSubSessionToken, getSubSessionId, getSubSessionName, clearSubSession,
  onSubSessionExpired, announceSubSessionRenewed,
} from '../api/apiFetch.js';
import { CLINIC_DEFAULT_ROLE } from '../lib/clinicFunctions.js';

const PIN_LEN = 6;
const DECLINE_TTL_MS = 24 * 60 * 60 * 1000;    // a declined notice auto-clears 24h after it was decided
const SESSION_CHECK_MS = 60 * 1000;             // background status peek while the tab is visible
const SESSION_CHECK_GAP_MS = 15 * 1000;         // focus/visibility checks closer than this are skipped
const SESSION_TOUCH_GAP_MS = 5 * 60 * 1000;     // interaction refreshes the server session at most this often

const overlay = {
  position: 'fixed', inset: 0, zIndex: 4000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(20, 30, 45, 0.45)',
  backdropFilter: 'blur(18px) saturate(140%)', WebkitBackdropFilter: 'blur(18px) saturate(140%)',
  padding: 16,
};
const card = {
  width: '100%', maxWidth: 380, background: 'var(--surface)',
  border: '1px solid var(--border)', borderRadius: 16, padding: 28,
  boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
};

function PinDots({ length }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, minHeight: 16 }}>
      {Array.from({ length: PIN_LEN }).map((_, i) => (
        <span key={i} style={{
          width: 12, height: 12, borderRadius: '50%',
          background: i < length ? 'var(--brand)' : 'transparent',
          border: '1.5px solid ' + (i < length ? 'var(--brand)' : 'var(--border-strong)'),
          transition: 'background 120ms, border-color 120ms',
        }} />
      ))}
    </div>
  );
}

function Keypad({ onDigit, onBackspace, disabled }) {
  const keys = ['1','2','3','4','5','6','7','8','9','','0','⌫'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 18 }}>
      {keys.map((k, i) => k ? (
        <button key={i} type="button" disabled={disabled} className="btn"
          onClick={() => (k === '⌫' ? onBackspace() : onDigit(k))}
          style={{
            padding: '14px 0', fontSize: 20, fontWeight: 500,
            background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 12,
            color: k === '⌫' ? 'var(--text-3)' : 'var(--text)',
            cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{k}</button>
      ) : <div key={i} />)}
    </div>
  );
}

// Numeric PIN input: digit/backspace via keyboard or keypad, plus Enter → submit
// when full. Input-only (no submit coupling), so components own their submit.
function usePin(busy, onEnter) {
  const [pin, setPin] = useState('');
  useEffect(() => {
    function onKey(e) {
      if (busy) return;
      if (/^\d$/.test(e.key)) setPin(p => (p.length >= PIN_LEN ? p : p + e.key));
      else if (e.key === 'Backspace') setPin(p => p.slice(0, -1));
      else if (e.key === 'Enter' && pin.length === PIN_LEN) onEnter(pin);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pin, busy, onEnter]);
  const digit = (d) => setPin(p => (p.length >= PIN_LEN ? p : p + d));
  const backspace = () => setPin(p => p.slice(0, -1));
  return { pin, setPin, digit, backspace };
}

export default function SubIdentityGate({ children }) {
  const { user, logout, isImpersonating, impersonation, setImpersonationSubIdentity, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const isMailbox = user?.role === 'ClinicMailbox';
  // Assuming a mailbox identity: pick a person, never a PIN.
  const viewingAs = isMailbox && isImpersonating;

  const [signedIn, setSignedIn] = useState(() => !!getSubSessionToken());
  const [stage, setStage] = useState('loading');   // loading | pick | register | pending | error
  const [ctx, setCtx] = useState(null);
  const [selectedId, setSelectedId] = useState('');   // '' = no selection (default on lock)
  const [registerInitial, setRegisterInitial] = useState(null);   // prefill for "Request again"
  const [checking, setChecking] = useState(false);   // re-reading state after a name is picked
  const [error, setError] = useState(null);

  // Clearing a declined notice is recorded server-side so every terminal in the
  // clinic drops it. Hide it here immediately (optimistic); fire-and-forget the
  // write — a failure just means it reappears on the next load, which is safe.
  function dismissDeclined(subId) {
    setCtx(c => ({ ...c, roster: (c?.roster || []).map(r => r.subId === subId ? { ...r, dismissedAt: new Date().toISOString() } : r) }));
    dismissRosterDeclined(subId).catch(() => {});
  }

  const load = useCallback(async () => {
    setStage('loading'); setError(null); setSelectedId('');
    try {
      const data = await getClinicContext();
      setCtx(data);
      setStage('pick');
    } catch (e) {
      setError(e.message || 'Could not load your clinic.');
      setStage('error');
    }
  }, []);

  // Signed in = a verified sub-session (real login) or a picked person (assumed).
  const resolved = viewingAs ? !!impersonation?.subId : signedIn;

  useEffect(() => { if (isMailbox && !resolved) load(); }, [isMailbox, resolved, load]);

  if (!isMailbox) return children;
  // Signed in for real: keep watching the session. (Viewing as a person has no
  // session to lapse — the person is a header, not a PIN.)
  if (resolved) return viewingAs ? children : <>{children}<SubSessionGuard onSignOut={() => logout?.()} /></>;

  // Always reachable while this overlay is up — it covers the banner's Exit.
  const exitAssumed = () => { exitImpersonation(); window.location.assign('/cases'); };
  const footerAction = viewingAs
    ? { label: 'Exit assumed identity', onClick: exitAssumed }
    : { label: 'Sign out', onClick: () => logout?.() };

  // Every fresh sign-in lands on All Cases — never the previous person's link.
  const onSignedIn = (token, name, clinic, jobTitle, subId) => {
    setSubSession(token, name, clinic, jobTitle, subId);
    navigate('/cases', { replace: true });
    setSignedIn(true);
  };

  const roster = ctx?.roster || [];
  const selectable = roster.filter(r => r.selectable);
  const pendingMine = roster.filter(r => r.status === 'pending');
  // Declined notices still worth showing. Drop any name that has a newer
  // pending/active row (a re-request supersedes it), any cleared on this
  // terminal, and any older than 24h (auto-clear). Roster is newest-first, so
  // keeping the first per name yields the most recent decline.
  const livingNames = new Set(
    roster.filter(r => r.status === 'active' || r.status === 'pending')
          .map(r => r.displayName.toLowerCase()),
  );
  const now = Date.now();
  const seenDeclined = new Set();
  const declinedMine = roster.filter(r => {
    if (r.status !== 'declined') return false;
    const name = r.displayName.toLowerCase();
    if (livingNames.has(name) || r.dismissedAt || seenDeclined.has(name)) return false;
    if (r.decidedAt && now - new Date(r.decidedAt).getTime() > DECLINE_TTL_MS) return false;
    seenDeclined.add(name);
    return true;
  });
  const selected = selectable.find(r => r.subId === selectedId) || null;

  async function onDropdown(v) {
    setError(null);
    if (v === '__register') { setRegisterInitial(null); setStage('register'); setSelectedId(''); return; }
    setSelectedId(v);
    // Assumed identity: the choice IS the sign-in — no PIN, nothing to verify.
    if (viewingAs) {
      const entry = selectable.find(r => r.subId === v);
      if (!entry) return;
      setImpersonationSubIdentity(entry);
      navigate('/cases', { replace: true });
      return;
    }
    // Read the CURRENT server state for this person before showing the keypad — a
    // supervisor may have just reset their PIN (or removed them). Routing to
    // enter-PIN vs set-PIN must follow fresh truth, not the last snapshot, so a
    // just-reset PIN never lands the user on "enter PIN" → wrong-PIN.
    setChecking(true);
    try { setCtx(await getClinicContext()); }
    catch { /* keep the snapshot; the verify endpoint still guards */ }
    finally { setChecking(false); }
  }

  function requestAgain(entry) {
    setRegisterInitial({ fullName: entry.displayName, jobTitle: entry.jobTitle });
    setStage('register');
    setSelectedId('');
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label={viewingAs ? 'Assumed identity — pick a person' : 'Clinic sign-in'}>
      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 12 }}>
          {viewingAs ? `Assuming · ${ctx?.displayName || ctx?.clinicName || impersonation?.email || ''}`
                     : (ctx?.displayName || ctx?.clinicName || 'Clinic sign-in')}
        </div>

        {stage === 'loading' && (
          <>
            <p style={{ color: 'var(--text-2)' }}>Loading…</p>
            {viewingAs && (
              <button className="btn ghost" onClick={exitAssumed}
                style={{ width: '100%', marginTop: 8, fontSize: 12, color: 'var(--text-3)', justifyContent: 'center' }}>
                Exit assumed identity
              </button>
            )}
          </>
        )}

        {stage === 'error' && (
          <>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Something went wrong</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{error}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn primary" onClick={load}>Try again</button>
              <button className="btn ghost" onClick={footerAction.onClick}>{footerAction.label}</button>
            </div>
          </>
        )}

        {stage === 'pick' && viewingAs && (
          <>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Who are you viewing as?</h2>
            <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '6px 0 14px' }}>
              This mailbox is shared — its cases belong to the person who reported them.
              Pick whose view to look through. No PIN is needed, and nothing can be saved.
            </p>

            {selectable.length > 0
              ? <UserDropdown items={selectable} value={selectedId} onChange={onDropdown} allowRegister={false} />
              : (
                <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
                  No one is registered at this clinic yet, so there is nothing to view.
                </p>
              )}

            <button className="btn ghost" onClick={exitAssumed}
              style={{ width: '100%', marginTop: 16, fontSize: 12, color: 'var(--text-3)', justifyContent: 'center' }}>
              Exit assumed identity
            </button>
          </>
        )}

        {stage === 'pick' && !viewingAs && (
          <>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Who's signing in?</h2>
            <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '6px 0 14px' }}>Select your name, then enter your PIN.</p>

            <UserDropdown items={selectable} value={selectedId} onChange={onDropdown} />

            {selected
              ? (checking
                  ? <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 14 }}>Checking…</p>
                  : selected.hasPin
                      ? <PinEntry key={selected.subId} entry={selected} onSignedIn={onSignedIn} onRefresh={load} />
                      : <PinSetup key={selected.subId} entry={selected} onSignedIn={onSignedIn} />)
              : (
                <>
                  {(pendingMine.length > 0 || declinedMine.length > 0) && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {pendingMine.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ color: 'var(--text-3)', fontSize: 12, minWidth: 0 }}>
                            Awaiting approval: {pendingMine.map(r => r.displayName).join(', ')}
                          </span>
                          <button type="button" onClick={load}
                            style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand-ink)', fontSize: 12, fontWeight: 600, padding: 0 }}>
                            Refresh
                          </button>
                        </div>
                      )}
                      {declinedMine.map(r => (
                        <div key={r.subId} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                            <strong>{r.displayName}</strong>'s request was declined{r.declineReason ? ':' : '.'}
                          </div>
                          {r.declineReason && (
                            <div style={{ fontSize: 12.5, color: 'var(--text-3)', fontStyle: 'italic', marginTop: 3, overflowWrap: 'anywhere' }}>
                              "{r.declineReason}"
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                            <button type="button" onClick={() => requestAgain(r)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand-ink)', fontSize: 12.5, fontWeight: 600, padding: 0 }}>
                              Request again
                            </button>
                            <button type="button" onClick={() => dismissDeclined(r.subId)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 12.5, fontWeight: 600, padding: 0 }}>
                              Dismiss
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button className="btn ghost" onClick={() => logout?.()}
                    style={{ width: '100%', marginTop: 16, fontSize: 12, color: 'var(--text-3)', justifyContent: 'center' }}>
                    Sign out
                  </button>
                </>
              )}
          </>
        )}

        {stage === 'register' && (
          <RegisterForm
            initial={registerInitial}
            onDone={(entry) => { setRegisterInitial(null); setCtx(c => ({ ...c, roster: [entry, ...(c?.roster || [])] })); setStage('pending'); }}
            onBack={() => { setRegisterInitial(null); setStage('pick'); }}
          />
        )}

        {stage === 'pending' && (
          <>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Request sent</h2>
            <p style={{ color: 'var(--text-2)', fontSize: 14 }}>
              A supervisor will confirm your name. Once approved you'll set up a PIN and can sign in.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn primary" onClick={load}>Back to login</button>
              <button className="btn ghost" onClick={footerAction.onClick}>{footerAction.label}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Watches a signed-in clinic session and, once it lapses, asks for the same
// person's PIN over the current page (see the header comment).
function SubSessionGuard({ onSignOut }) {
  const location = useLocation();
  const [expired, setExpired] = useState(false);
  const lastCheck = useRef(0);
  const lastTouch = useRef(Date.now());   // sign-in / page load just touched it
  const expiredRef = useRef(false);
  expiredRef.current = expired;

  // A verdict only counts for the token it was asked about: a check still in
  // flight when the PIN is re-entered must not reopen the pad over the new session.
  const lapsedIfStill = (sentToken, s) => {
    if (s?.isClinicMailbox && s.subSessionValid === false && getSubSessionToken() === sentToken) setExpired(true);
  };

  const check = useCallback(async (force = false) => {
    const sentToken = getSubSessionToken();
    if (!sentToken) return;
    const now = Date.now();
    if (!force && now - lastCheck.current < SESSION_CHECK_GAP_MS) return;
    lastCheck.current = now;
    try { lapsedIfStill(sentToken, await getClinicSessionStatus()); }
    catch { /* offline or a blip — a refused write still raises the PIN pad */ }
  }, []);

  useEffect(() => onSubSessionExpired(() => setExpired(true)), []);
  // The person is working (typing a long description makes no API calls):
  // refresh the server session, throttled. Never while the PIN pad is up — those
  // keys are the PIN, and the session is already gone.
  useEffect(() => {
    const onActivity = async () => {
      const sentToken = getSubSessionToken();
      if (expiredRef.current || !sentToken) return;
      const now = Date.now();
      if (now - lastTouch.current < SESSION_TOUCH_GAP_MS) return;
      lastTouch.current = now;
      lastCheck.current = now;
      try { lapsedIfStill(sentToken, await getClinicSessionStatus({ touch: true })); }
      catch { /* a blip — the next check or a refused write still catches it */ }
    };
    const events = ['keydown', 'pointerdown', 'input'];
    events.forEach(e => window.addEventListener(e, onActivity, { capture: true, passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, onActivity, { capture: true }));
  }, []);
  // Every page change — opening a new submission included — starts on a live session.
  useEffect(() => { check(true); }, [location.pathname, check]);
  useEffect(() => {
    const whenVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', whenVisible);
    window.addEventListener('focus', whenVisible);
    const timer = setInterval(whenVisible, SESSION_CHECK_MS);
    return () => {
      document.removeEventListener('visibilitychange', whenVisible);
      window.removeEventListener('focus', whenVisible);
      clearInterval(timer);
    };
  }, [check]);

  if (!expired) return null;
  // Portalled outside #root so the page underneath can go inert without the pad.
  return createPortal(
    <RenewSession
      onSignOut={onSignOut}
      onRenewed={() => { setExpired(false); lastCheck.current = lastTouch.current = Date.now(); announceSubSessionRenewed(); }}
    />,
    document.body,
  );
}

// The PIN pad for the person who was signed in. Their page stays mounted
// underneath; a different person must use "Switch user" (which does discard the
// page — someone else's half-typed case must not be finished under their name).
function RenewSession({ onRenewed, onSignOut }) {
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { setCtx(await getClinicContext()); }
    catch (e) { setError(e.message || 'Could not load your clinic.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // The page stays mounted underneath, so take the keyboard off it: the PIN pad
  // listens on window, and digits (or Enter) must not land in a focused field or
  // submit a form below. Focus moves into the dialog and the root goes inert.
  const dialogRef = useRef(null);
  useEffect(() => {
    document.activeElement?.blur?.();
    dialogRef.current?.focus();
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');
    return () => root?.removeAttribute('inert');
  }, []);

  const subId = getSubSessionId();
  const name = getSubSessionName() || '';
  // Sessions saved before the subId was stored are matched by name.
  const entry = (ctx?.roster || []).find(r => r.selectable && (subId
    ? r.subId === subId
    : r.displayName?.toLowerCase() === name.toLowerCase())) || null;
  const firstName = name.split(/\s+/)[0] || '';

  const onSignedIn = (token, displayName, clinic, jobTitle, sid) => {
    setSubSession(token, displayName, clinic, jobTitle, sid);
    onRenewed();
  };
  const switchUser = () => { clearSubSession(); window.location.assign('/cases'); };
  const linkBtn = { width: '100%', marginTop: 8, fontSize: 12, color: 'var(--text-3)', justifyContent: 'center' };

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Session timed out — enter your PIN">
      <div style={{ ...card, outline: 'none' }} ref={dialogRef} tabIndex={-1}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 12 }}>
          {ctx?.displayName || ctx?.clinicName || 'Clinic sign-in'}
        </div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{firstName ? `Still there, ${firstName}?` : 'Still there?'}</h2>
        <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '6px 0 0' }}>
          Your session timed out. Enter your PIN to carry on — anything you've typed is kept.
        </p>

        {error && (
          <>
            <div role="alert" className="callout danger" style={{ marginTop: 14, fontSize: 12, padding: '8px 10px' }}><div style={{ flex: 1 }}>{error}</div></div>
            <button className="btn primary" onClick={load} style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}>Try again</button>
          </>
        )}
        {!error && !ctx && <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 14 }}>Loading…</p>}
        {!error && ctx && (entry
          ? (entry.hasPin
              ? <PinEntry key={entry.subId} entry={entry} onSignedIn={onSignedIn} onRefresh={load} />
              : <PinSetup key={entry.subId} entry={entry} onSignedIn={onSignedIn} />)
          : (
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 14 }}>
              {name ? <><strong>{name}</strong> can no longer sign in at this clinic.</> : 'This session can no longer be resumed.'} Ask a supervisor, or switch user.
            </p>
          ))}

        <button className="btn ghost" onClick={switchUser} style={{ ...linkBtn, marginTop: 16 }}>
          {firstName ? `Not ${firstName}? Switch user` : 'Switch user'}
        </button>
        <button className="btn ghost" onClick={onSignOut} style={linkBtn}>Sign out</button>
      </div>
    </div>
  );
}

// Custom (non-native) dropdown so the picker matches the app's styling rather
// than the OS menu. Options are the active roster + a "Register" action.
function UserDropdown({ items, value, onChange, allowRegister = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const selected = items.find(r => r.subId === value) || null;
  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="input" onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox" aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 15, padding: '10px 12px', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'var(--text)' : 'var(--text-3)' }}>
          {selected ? selected.displayName : 'Select user…'}
        </span>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-3)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div role="listbox" className="plain-pop dd-shell">
          {items.map(r => (
            <button key={r.subId} type="button" role="option" aria-selected={r.subId === value} onClick={() => pick(r.subId)}
              className={`dd-item dd-two${r.subId === value ? ' selected' : ''}`}>
              <b>{r.displayName}</b>
              <span>{[r.jobTitle, !r.hasPin ? 'set up PIN' : null].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
          {allowRegister && (
            <>
              {items.length > 0 && <div className="dd-sep" />}
              <button type="button" onClick={() => pick('__register')} className="dd-item dd-create">
                ＋ Register a new name
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PinEntry({ entry, onSignedIn, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const submit = useCallback(async (pin) => {
    setBusy(true); setErr(null);
    try { const { token, clinicName } = await verifySubSession(entry.subId, pin); onSignedIn(token, entry.displayName, clinicName, entry.jobTitle, entry.subId); }
    catch (e) { setErr(e.message || 'Incorrect PIN.'); setPin(''); setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, onSignedIn]);
  const { pin, setPin, digit, backspace } = usePin(busy, submit);

  async function forgot() {
    setBusy(true); setErr(null);
    try { await requestSubPinReset(entry.subId); setResetSent(true); }
    catch (e) { setErr(e.message || 'Could not send the reset request.'); }
    finally { setBusy(false); }
  }
  if (resetSent) return (
    <>
      <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 14 }}>
        A supervisor will reset the PIN for <strong>{entry.displayName}</strong>. Once it's done, refresh and set a new PIN.
      </p>
      <button className="btn primary" onClick={() => onRefresh?.()} style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}>
        Refresh
      </button>
    </>
  );
  return (
    <>
      <PinDots length={pin.length} />
      {err && <div role="alert" className="callout danger" style={{ marginTop: 14, fontSize: 12, padding: '8px 10px' }}><div style={{ flex: 1 }}>{err}</div></div>}
      <Keypad onDigit={digit} onBackspace={backspace} disabled={busy} />
      <button className="btn primary" style={{ width: '100%', marginTop: 16, padding: '12px 0', justifyContent: 'center' }}
        disabled={busy || pin.length < PIN_LEN} onClick={() => submit(pin)}>{busy ? 'Please wait…' : 'Unlock'}</button>
      <button className="btn ghost" onClick={forgot} disabled={busy}
        style={{ width: '100%', marginTop: 8, fontSize: 12, color: 'var(--brand-ink)', justifyContent: 'center' }}>Forgot your PIN?</button>
    </>
  );
}

function PinSetup({ entry, onSignedIn }) {
  const [phase, setPhase] = useState('enter');   // enter → confirm
  const [first, setFirst] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = useCallback(async (pin) => {
    if (phase === 'enter') {
      if (/^(\d)\1+$/.test(pin)) { setErr('PIN cannot be a single repeating digit.'); setPinVal(''); return; }
      setFirst(pin); setPhase('confirm'); setPinVal('');
      return;
    }
    if (pin !== first) { setErr('PINs do not match. Start over.'); setFirst(''); setPhase('enter'); setPinVal(''); return; }
    setBusy(true); setErr(null);
    try {
      await setSubPin(entry.subId, pin);
      const { token, clinicName } = await verifySubSession(entry.subId, pin);
      onSignedIn(token, entry.displayName, clinicName, entry.jobTitle, entry.subId);
    } catch (e) { setErr(e.message || 'Could not set your PIN.'); setBusy(false); setPinVal(''); setPhase('enter'); setFirst(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, first, entry, onSignedIn]);
  const { pin, setPin: setPinVal, digit, backspace } = usePin(busy, submit);

  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '12px 0 0' }}>
        {phase === 'enter'
          ? <>Choose a 6-digit PIN for <strong>{entry.displayName}</strong>.</>
          : 'Type the same PIN again to confirm.'}
      </p>
      <PinDots length={pin.length} />
      {err && <div role="alert" className="callout danger" style={{ marginTop: 14, fontSize: 12, padding: '8px 10px' }}><div style={{ flex: 1 }}>{err}</div></div>}
      <Keypad onDigit={digit} onBackspace={backspace} disabled={busy} />
      <button className="btn primary" style={{ width: '100%', marginTop: 16, padding: '12px 0', justifyContent: 'center' }}
        disabled={busy || pin.length < PIN_LEN} onClick={() => submit(pin)}>
        {busy ? 'Please wait…' : (phase === 'enter' ? 'Continue' : 'Set PIN & sign in')}
      </button>
    </>
  );
}

// Capitalise the first letter of each word ("marius cleepa" → "Marius Cleepa").
function titleCase(s) {
  return String(s || '').replace(/\b[\p{L}]/gu, c => c.toUpperCase());
}

function RegisterForm({ onDone, onBack, initial }) {
  const initialWords = String(initial?.fullName || '').trim().split(/\s+/).filter(Boolean);
  const [firstName, setFirstName] = useState(initialWords[0] || '');
  const [surname, setSurname] = useState(initialWords.slice(1).join(' '));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const fullName = [firstName, surname].map(s => titleCase(s.trim())).filter(Boolean).join(' ');
  const ready = firstName.trim() && surname.trim();

  async function submit(e) {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true); setErr(null);
    try { const { entry } = await requestClinicRoster({ fullName, jobTitle: CLINIC_DEFAULT_ROLE, note: note.trim() }); onDone(entry); }
    catch (e2) { setErr(e2.message || 'Could not send your request.'); setBusy(false); }
  }
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginTop: 12 };
  return (
    <form onSubmit={submit}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Create your account</h2>
      <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 0 }}>A supervisor confirms your name first.</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ ...labelStyle, flex: 1 }}>First name
          <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)}
            onBlur={() => setFirstName(v => titleCase(v.trim()))} autoFocus maxLength={100} style={{ marginTop: 4, width: '100%' }} /></label>
        <label style={{ ...labelStyle, flex: 1 }}>Surname
          <input className="input" value={surname} onChange={e => setSurname(e.target.value)}
            onBlur={() => setSurname(v => titleCase(v.trim()))} maxLength={100} style={{ marginTop: 4, width: '100%' }} /></label>
      </div>
      <label style={labelStyle}>Note (optional)
        <textarea className="input" value={note} onChange={e => setNote(e.target.value)} maxLength={500} rows={2}
          placeholder="Anything the supervisor should know (e.g. covering this week)"
          style={{ marginTop: 4, width: '100%', resize: 'vertical' }} /></label>
      {err && <div role="alert" className="callout danger" style={{ marginTop: 12, fontSize: 12, padding: '8px 10px' }}><div style={{ flex: 1 }}>{err}</div></div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button type="submit" className="btn primary" disabled={!ready || busy}>{busy ? 'Sending…' : 'Request'}</button>
        <button type="button" className="btn ghost" onClick={onBack}>Back</button>
      </div>
    </form>
  );
}
